import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useFillPrescription } from '../contexts/fill/use-fill-prescription';
import {
    CoverageCardType,
    SubmitCoverageAttestationDocument,
} from '../graphql/generated/graphql';
import { getAbsoluteRoute } from '../util/get-absolute-route';

export type UseCoverageAssistanceMutationParams = {
    cardType: CoverageCardType;
    declineCallback?: () => void;
    drxId: string;
};

export const useCoverageAssistanceMutation = ({
    cardType,
    declineCallback,
    drxId,
}: UseCoverageAssistanceMutationParams) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setCoverageCard } = useFillPrescription();

    const [submitAttestation, { data, loading }] = useMutation(
        SubmitCoverageAttestationDocument
    );

    const accept = useCallback(async () => {
        const result = await submitAttestation({
            variables: {
                drxId,
                coverageCardType: cardType,
                attestation: true,
            },
        });
        if (result.data?.generateCoverageCard.coverageCard) {
            setCoverageCard(result.data.generateCoverageCard.coverageCard);
        }
        navigate({
            pathname: getAbsoluteRoute('fill.drxId.pharmacy.root', { drxId }),
            search: `?${searchParams.toString()}`,
        });
    }, [
        cardType,
        drxId,
        navigate,
        searchParams,
        setCoverageCard,
        submitAttestation,
    ]);

    const decline = useCallback(async () => {
        await submitAttestation({
            variables: {
                drxId,
                coverageCardType: cardType,
                attestation: false,
            },
        });
        declineCallback?.();
    }, [cardType, declineCallback, drxId, submitAttestation]);

    return {
        accept,
        data,
        decline,
        loading,
    };
};
